import React from 'react';
import { Row } from '@cuemath/comps';
import { Button, Text } from '@cuemath/web-comps';
import styled from 'styled-components';
import { trackEvent } from 'modules/analytics';

import { POCValue, POCWrapper } from 'screens/student-details/styled-comps';
import { CopyButton } from 'comps';
import { STUDENT_PAYMENT_LINK_REFUND_URL, STUDENT_FEE_REFUND_URL } from 'constants/api';
import { PAYMENT_STATUS } from 'constants/form';
import { convertEpochToDateFormat, formatTimestamp } from 'utils';
import { connect } from 'react-redux';
import { EVENT_TYPE } from 'constants/logger';
import { COLORS } from '@cuemath/comps';

const PaymentInfoHeading = styled(Text)`
  min-width: ${({ minWidth }) => (minWidth ? minWidth : '152px')};
  margin-right: 16px;
  color: rgba(43, 43, 43, 0.75);
  line-height: 20px;
  cursor: ${({ cursor }) => (cursor ? cursor : 'default')};
  width: ${({ width }) => (width ? width : 'auto')};
`;

const CustomRow = styled(Row)`
  margin-top: ${({ marginTop }) => (marginTop ? marginTop : '20px')};
  flex-wrap: nowrap;
  align-items: center;
`;

const PaymentStatusButton = styled(Button)`
  background-color: ${COLORS.AMBER};
  padding: 6px 14px;
  font-size: 14px;
  width: fit-content;
`;

const AnchorTag = styled.a`
  color: black;
  text-decoration: underline;
  :hover {
    color: black;
  }
`;

const POCValueWrapper = styled.div`
  max-width: 182px;
  overflow-wrap: break-word;
`;

function PaymentInfo(props) {
  const {
    payment_type: paymentType,
    id: paymentId,
    currency,
    invoice_id: invoiceId,
    credits_applied: creditsApplied,
    credits_used: creditsUsed,
    created_on_ts: createdOn,
    classes_per_week: classesPerWeek,
    created_by_details: createdByDetails,
    courses,
    is_student_fee: isStudentFee,
    cuemath_invoice_url: cuemathInvoiceUrl,
    is_refunded: isRefunded,
    classes_extension_category: classExtensionCategory,
    rack_rate: rackRate,
    state,
    is_payment_refundable: isPaymentRefundable,
    product_type_text: productTypeText,
    no_of_classes: noOfClasses,
    userId,
  } = props || {};

  const { first_name: firstName, last_name: lastName } = createdByDetails || {};

  const formattedCreatedOnsDate = createdOn && convertEpochToDateFormat(createdOn * 1000);
  const formattedCreatedOnTs = createdOn && formatTimestamp(createdOn);

  const onCreateRefund = () => {
    const refundUrl =
      (isStudentFee ? STUDENT_FEE_REFUND_URL : STUDENT_PAYMENT_LINK_REFUND_URL) +
      paymentId;

    trackEvent(EVENT_TYPE.CREATE_REFUND_CLICK, {
      refund_url: refundUrl,
      student_payment_link_id: paymentId,
    });

    window.open(refundUrl, '_blank');
  };

  const openPaymentStatusTab = () => {
    const paymentStatusUrl = `https://admin.cuemath.com/payment/admin/transaction/?flt_order_id_equals=${paymentId}`;
    window.open(paymentStatusUrl, '_blank');
  };

  return (
    <POCWrapper>
      <CustomRow>
        <PaymentInfoHeading variant="eyebrow2">NUMBER OF CLASSES</PaymentInfoHeading>
        <POCValue variant="body1Medium">{noOfClasses || '-'}</POCValue>
      </CustomRow>
      <CustomRow>
        <PaymentInfoHeading variant="eyebrow2">CLASSES PER WEEK</PaymentInfoHeading>
        <POCValue variant="body1Medium">{classesPerWeek || '-'}</POCValue>
      </CustomRow>
      <CustomRow>
        <PaymentInfoHeading variant="eyebrow2">PAYMENT TYPE</PaymentInfoHeading>
        <POCValue variant="body1Medium">
          {paymentType?.replaceAll('_', ' ').toLowerCase()}
        </POCValue>
      </CustomRow>
      <CustomRow>
        <PaymentInfoHeading variant="eyebrow2">Course Name</PaymentInfoHeading>
        <POCValueWrapper>
          <POCValue variant="body1Medium">{productTypeText || '-'}</POCValue>
        </POCValueWrapper>
      </CustomRow>
      <CustomRow>
        <PaymentInfoHeading variant="eyebrow2">Extension Category</PaymentInfoHeading>
        <POCValueWrapper>
          <POCValue variant="body1Medium">{classExtensionCategory || '-'}</POCValue>
        </POCValueWrapper>
      </CustomRow>
      <CustomRow>
        <PaymentInfoHeading variant="eyebrow2">PAYMENT ID</PaymentInfoHeading>
        <PaymentStatusButton size="small" onClick={openPaymentStatusTab}>
          <Text variant="cta2">Check status</Text>
        </PaymentStatusButton>
      </CustomRow>
      <CustomRow marginTop="4px">
        <CopyButton copyMessage="Copy Payment Id" text={paymentId} />
      </CustomRow>
      <CustomRow justifyContent={invoiceId && 'space-between'}>
        <PaymentInfoHeading minWidth={invoiceId && '88px'} variant="eyebrow2">
          INVOICE ID
        </PaymentInfoHeading>
        {cuemathInvoiceUrl ? (
          <POCValue variant="body1Medium">
            <Text
              variant="body1Medium"
              onClick={() =>
                trackEvent(EVENT_TYPE.DOWNLOAD_INVOICE_CLICK, {
                  student_payment_link_id: paymentId,
                  cuemath_invoice_url: cuemathInvoiceUrl,
                })
              }
            >
              <AnchorTag
                href={cuemathInvoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download Invoice
              </AnchorTag>
            </Text>
          </POCValue>
        ) : (
          <Text variant="body2">-</Text>
        )}
      </CustomRow>
      {invoiceId && (
        <CustomRow marginTop="4px">
          <CopyButton copyMessage="Copy Invoice Id" text={invoiceId} />
        </CustomRow>
      )}
      <CustomRow>
        <PaymentInfoHeading variant="eyebrow2">GROSS AMOUNT</PaymentInfoHeading>
        <POCValue variant="body1Medium">
          {currency && rackRate ? `${currency} ${rackRate}` : '-'}
        </POCValue>
      </CustomRow>
      <CustomRow>
        <PaymentInfoHeading variant="eyebrow2">CREDIT APPLIED</PaymentInfoHeading>
        <POCValue variant="body1Medium">{creditsApplied || '-'}</POCValue>
      </CustomRow>
      <CustomRow>
        <PaymentInfoHeading variant="eyebrow2">CREDIT USED</PaymentInfoHeading>
        <POCValue variant="body1Medium">{creditsUsed || '-'}</POCValue>
      </CustomRow>
      <CustomRow>
        <PaymentInfoHeading variant="eyebrow2">CREATED ON</PaymentInfoHeading>
        <POCValue variant="body1Medium">
          {formattedCreatedOnsDate && formattedCreatedOnTs
            ? `${formattedCreatedOnsDate}, ${formattedCreatedOnTs}`
            : '-'}
        </POCValue>
      </CustomRow>
      <CustomRow>
        <PaymentInfoHeading variant="eyebrow2">CREATED BY</PaymentInfoHeading>
        <POCValue variant="body1Medium">
          {firstName || '-'} {lastName || '-'}
        </POCValue>
      </CustomRow>
      {state === PAYMENT_STATUS.CONFIRMED && (
        <CustomRow justifyContent="center">
          <Button size="xsmall" onClick={onCreateRefund} isDisabled={isRefunded}>
            <Text variant="cta2">Create refund</Text>
          </Button>
        </CustomRow>
      )}
    </POCWrapper>
  );
}

export default connect(({ auth: { userId } = {} }) => ({
  userId,
}))(PaymentInfo);
